import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ChatMessage, ConversationState } from "@/components/agent/chat/types";
import {
  saveConversationMessagesOnClose,
  saveSystemMessage,
  updateConversationState,
} from "@/services/messages";
import { Tables } from "@/types/database.types";

/**
 * IMPORTANT WARNING: Database Type Mismatch
 *
 * The TypeScript types in database.types.ts are not in sync with the actual database schema.
 * The TypeScript types expect messages to have a 'role' field, but the actual database
 * uses 'sender_type'. This causes errors like "Could not find the 'role' column in schema cache".
 *
 * To fix this properly, either:
 * 1. Update the database schema to match the TypeScript types (add 'role' column)
 * 2. Update the TypeScript types to match the actual database (use 'sender_type')
 *
 * Until then, we use type assertions (as any) in the messages.ts service layer
 * to work around these errors.
 */

/**
 * IMPORTANT NOTE: There is a mismatch between the TypeScript types and the actual database schema.
 *
 * The database uses 'sender_type' with values "ai", "user", "agent_chat_user", while our UI uses:
 * - "user" -> for user messages
 * - "agent" -> for AI assistant messages
 * - "system" -> for system notifications
 *
 * When converting between them:
 * - Database "user" -> UI "user"
 * - Database "ai" (with is_system_message in metadata) -> UI "system"
 * - Database "ai" (without special metadata) -> UI "agent"
 */

// Define type for database message to match actual schema
type DbMessageActual = {
  id: string;
  content: string;
  conversation_id: string;
  created_at: string;
  metadata: any;
  sender_type: "user" | "ai" | "agent_chat_user";
  sender_id?: string | null;
  openai_message_id?: string | null;
};

// Define type for extracted field values
type ExtractedFieldValue = {
  field_id: string;
  value: string;
};

export const useConversationClosing = (
  conversationId: string | null,
  setChatMessages: (messages: ChatMessage[]) => void,
  setConversationState: (state: ConversationState) => void,
  agentId: string,
  onError?: (message: string) => void
) => {
  const { toast } = useToast();
  const [isClosing, setIsClosing] = useState(false);

  const handleCloseConversation = async () => {
    if (!conversationId || isClosing) return;

    setIsClosing(true);

    try {
      // Update conversation state to 'completed' in the database
      const { error: updateError } = await updateConversationState(
        conversationId,
        "completed"
      );

      if (updateError) {
        throw updateError;
      }

      // Call the extract-form-fields function
      try {
        console.log(
          "[DEBUG-FIELD-VALUES] Manual closing: Calling extract-form-fields function"
        );
        const { data, error } = await supabase.functions.invoke(
          "extract-form-fields",
          {
            body: {
              conversationId,
              agentId,
            },
          }
        );

        if (error) {
          console.error(
            "[DEBUG-FIELD-VALUES] Error extracting form fields:",
            error
          );
        } else {
          console.log(
            "[DEBUG-FIELD-VALUES] Form fields extracted and saved successfully by edge function"
          );
          // Field values and summary are now saved by the edge function
        }
      } catch (err) {
        console.error(
          "[DEBUG-FIELD-VALUES] Error calling extract-form-fields:",
          err
        );
      }

      // Add a system message indicating the conversation was manually closed
      const systemMessage: ChatMessage = {
        type: "system",
        content: "Conversation completed and submitted",
        timestamp: new Date().toISOString(),
      };

      // Get current messages and append the new system message
      const { data: messagesData } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true });

      // Transform to ChatMessage format if data exists, or use existing messages
      let currentMessages: ChatMessage[] = [];
      if (messagesData && messagesData.length > 0) {
        currentMessages = messagesData.map((msgData) => {
          // Cast to the correct type
          const msg = msgData as unknown as DbMessageActual;

          // Check if this is a system message by looking at metadata
          if (
            msg.metadata &&
            typeof msg.metadata === "object" &&
            "is_system_message" in msg.metadata
          ) {
            return {
              type: "system",
              content: msg.content,
              timestamp: msg.created_at,
            };
          }

          // Normal message mapping
          return {
            type: msg.sender_type === "user" ? "user" : "agent",
            content: msg.content,
            timestamp: msg.created_at,
          };
        });
      }

      // Add the system message to the current messages and update the UI
      const updatedMessages = [...currentMessages, systemMessage];
      setChatMessages(updatedMessages);

      // Also save the system message to the database
      await saveSystemMessage(conversationId, systemMessage.content);

      // Save all messages to ensure everything is stored correctly
      await saveConversationMessagesOnClose(conversationId, updatedMessages);

      // Set conversation state to completed so the ThankYouScreen appears
      setConversationState("completed");
    } catch (error) {
      console.error("Error closing conversation:", error);
      if (onError) {
        onError("Failed to close conversation. Please try again.");
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to close conversation. Please try again.",
        });
      }
    } finally {
      setIsClosing(false);
    }
  };

  return { handleCloseConversation, isClosing };
};
